import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Sparkles, Loader2, Wand2, Link2, Palette, CheckCircle2, AlertCircle, ArrowRight, Copy, Paintbrush, Eye,
} from "lucide-react";
import { toast } from "sonner";

export default function ThemeGeneratorAdvanced() {
  const [mode, setMode] = useState("prompt");   // 'prompt' | 'url'
  const [directives, setDirectives] = useState([]);
  const [selectedDirectives, setSelectedDirectives] = useState([]);
  const [url, setUrl] = useState("");
  const [extraPrompt, setExtraPrompt] = useState("");
  const [nameHint, setNameHint] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);

  useEffect(() => {
    api.get("/themes/design-directives")
      .then(r => setDirectives(r.data))
      .catch(() => toast.error("Impossible de charger la bibliothèque de directives"));
  }, []);

  const toggleDirective = (key) => {
    setSelectedDirectives(cur => cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]);
  };

  const analyze = async () => {
    if (!url.trim()) return toast.error("Entre une URL à analyser");
    setAnalyzing(true); setAnalysis(null);
    try {
      const r = await api.post("/themes/analyze-inspiration-url", { url, directives: [] });
      setAnalysis(r.data);
      toast.success("Analyse terminée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Analyse échouée"); }
    finally { setAnalyzing(false); }
  };

  const generate = async () => {
    if (mode === "url" && !url.trim()) return toast.error("URL requise");
    if (mode === "prompt" && selectedDirectives.length === 0 && !extraPrompt.trim()) {
      return toast.error("Choisis au moins une directive ou écris un brief");
    }
    setGenerating(true); setGenerated(null);
    try {
      const endpoint = mode === "url" ? "/themes/ai-generate-from-url" : "/themes/ai-generate-advanced";
      const payload = { url: url || "", directives: selectedDirectives, extra_prompt: extraPrompt, name_hint: nameHint };
      const r = await api.post(endpoint, payload);
      setGenerated(r.data);
      toast.success(`Thème "${r.data.name}" généré`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération échouée"); }
    finally { setGenerating(false); }
  };

  const applyTheme = async () => {
    if (!generated?.id) return;
    try {
      await api.post(`/themes/${generated.id}/apply`);
      toast.success("Thème appliqué — rafraîchis la page pour voir le rendu");
    } catch { toast.error("Application échouée"); }
  };

  const downloadTheme = async () => {
    if (!generated?.id) return;
    try {
      const r = await api.get(`/themes/${generated.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `${generated.name.replace(/\s+/g, "-").toLowerCase()}.cockpit-theme.json`;
      a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Export échoué"); }
  };

  const groupByCategory = () => {
    const cats = {
      structure: ["logo_centered", "parallax_hero", "rounded_cards", "glassmorphism", "neumorphism"],
      dividers: ["wave_dividers", "diagonal_dividers"],
      interactions: ["icon_animations", "scroll_animations", "testimonial_carousel"],
      typography: ["editorial_typography", "brutalist_accent"],
      palette: ["soft_pastel", "dark_mode_premium", "gradient_buttons"],
    };
    const m = {};
    for (const [cat, keys] of Object.entries(cats)) {
      m[cat] = directives.filter(d => keys.includes(d.key));
    }
    return m;
  };

  const grouped = directives.length > 0 ? groupByCategory() : {};

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-6xl mx-auto" data-testid="theme-generator-advanced">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-violet-500 flex items-center justify-center">
          <Wand2 className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Theme Studio IA</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Génère des thèmes complets à partir d'un prompt, de directives visuelles ou d'un site d'inspiration.</p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 border-b border-white/10 pb-3">
        <Button
          variant={mode === "prompt" ? "default" : "outline"}
          onClick={() => setMode("prompt")}
          data-testid="mode-prompt-btn"
          className={mode === "prompt" ? "bg-gradient-to-r from-pink-500 to-violet-500" : ""}
        >
          <Sparkles className="w-4 h-4 mr-1.5" />Prompt + Directives
        </Button>
        <Button
          variant={mode === "url" ? "default" : "outline"}
          onClick={() => setMode("url")}
          data-testid="mode-url-btn"
          className={mode === "url" ? "bg-gradient-to-r from-sky-500 to-indigo-500" : ""}
        >
          <Link2 className="w-4 h-4 mr-1.5" />À partir d'une URL
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT — config */}
        <div className="lg:col-span-3 space-y-5">
          {mode === "url" && (
            <Card className="p-4 space-y-3">
              <Label>URL du site d'inspiration</Label>
              <div className="flex gap-2">
                <Input
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://stripe.com, https://linear.app…"
                  data-testid="inspiration-url-input"
                />
                <Button onClick={analyze} disabled={analyzing} variant="outline" data-testid="analyze-url-btn">
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  <span className="ml-1 hidden sm:inline">Analyser</span>
                </Button>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Le scraping extrait uniquement les <strong>directives visuelles</strong> (palette, typos, meta) —
                aucun texte ni image n'est copié.
              </p>
              {analysis && (
                <div className="mt-3 p-3 rounded-lg bg-[hsl(var(--muted))]/50 space-y-2" data-testid="analysis-result">
                  <div className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))]">Couleurs détectées</div>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.detected_colors.slice(0, 8).map(c => (
                      <div key={c} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5" title={c}>
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: c }} />
                        <code className="text-[10px] font-mono">{c}</code>
                      </div>
                    ))}
                  </div>
                  {analysis.detected_fonts?.length > 0 && (
                    <>
                      <div className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))] mt-2">Polices détectées</div>
                      <div className="flex flex-wrap gap-1.5">
                        {analysis.detected_fonts.slice(0, 5).map(f => <Badge key={f} variant="outline">{f}</Badge>)}
                      </div>
                    </>
                  )}
                </div>
              )}
            </Card>
          )}

          <Card className="p-4 space-y-3">
            <Label>Directives de design ({selectedDirectives.length} sélectionnée{selectedDirectives.length > 1 ? "s" : ""})</Label>
            <div className="space-y-3">
              {Object.entries(grouped).map(([cat, list]) => list.length > 0 && (
                <div key={cat}>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5">{cat}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map(d => (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() => toggleDirective(d.key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                          selectedDirectives.includes(d.key)
                            ? "bg-gradient-to-r from-pink-500 to-violet-500 border-transparent text-white"
                            : "border-white/20 hover:border-white/40 hover:bg-white/5"
                        }`}
                        title={d.prompt}
                        data-testid={`directive-${d.key}`}
                      >
                        {selectedDirectives.includes(d.key) && "✓ "}{d.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-4 space-y-3">
            <Label>Brief complémentaire (optionnel)</Label>
            <Textarea
              rows={4}
              value={extraPrompt}
              onChange={e => setExtraPrompt(e.target.value)}
              placeholder="Ex: Site d'avocat d'affaires parisien, ambiance feutrée, cible dirigeants 40-60 ans. Mets le logo au centre du hero."
              data-testid="extra-prompt-input"
            />
            <Label>Nom du thème (optionnel)</Label>
            <Input
              value={nameHint}
              onChange={e => setNameHint(e.target.value)}
              placeholder="Cabinet Lumière"
              data-testid="name-hint-input"
            />
            <Button
              onClick={generate}
              disabled={generating}
              size="lg"
              className="w-full bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 text-white"
              data-testid="generate-theme-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {generating ? "Génération en cours…" : "Générer le thème"}
            </Button>
          </Card>
        </div>

        {/* RIGHT — preview */}
        <div className="lg:col-span-2">
          {!generated && !generating && (
            <Card className="p-8 text-center space-y-2 sticky top-6">
              <Paintbrush className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))] opacity-40" />
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Le thème généré s'affichera ici.
              </p>
            </Card>
          )}
          {generating && (
            <Card className="p-8 text-center space-y-3 sticky top-6">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-pink-500" />
              <p className="text-sm">Claude Sonnet analyse ta demande…</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Environ 10-15 secondes.</p>
            </Card>
          )}
          {generated && (
            <Card className="p-0 overflow-hidden sticky top-6" data-testid="generated-theme-preview">
              {/* Preview gradient */}
              <div
                className="h-32 relative"
                style={{ background: generated.preview_gradient || `linear-gradient(135deg, ${generated.primary_color} 0%, ${generated.secondary_color} 100%)` }}
              >
                <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                  <div className="text-white">
                    <div className="text-[10px] uppercase tracking-widest opacity-80">Généré par Claude</div>
                    <div className="font-bold text-lg" style={{ fontFamily: generated.font_display }}>{generated.name}</div>
                  </div>
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-sm italic text-[hsl(var(--muted-foreground))]">{generated.description}</p>

                <div className="space-y-2">
                  <div className="text-xs font-bold uppercase tracking-wider">Palette</div>
                  <div className="grid grid-cols-7 gap-1">
                    {[
                      { k: "primary_color", l: "P" }, { k: "secondary_color", l: "S" }, { k: "accent_color", l: "A" },
                      { k: "background_color", l: "Bg" }, { k: "surface_color", l: "Sf" }, { k: "text_color", l: "Tx" }, { k: "muted_color", l: "Mu" },
                    ].map(x => (
                      <div key={x.k} className="flex flex-col items-center gap-1">
                        <div className="w-full aspect-square rounded border border-white/10" style={{ backgroundColor: generated[x.k] }} title={generated[x.k]} />
                        <span className="text-[9px] font-mono text-[hsl(var(--muted-foreground))]">{x.l}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-xs font-bold uppercase tracking-wider">Typographies</div>
                  <div className="text-xs"><strong>Display:</strong> {generated.font_display}</div>
                  <div className="text-xs"><strong>Body:</strong> {generated.font_body}</div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-[10px] font-mono">
                  <div className="p-2 bg-[hsl(var(--muted))]/50 rounded"><div className="opacity-60">Radius</div><div className="font-bold">×{generated.radius_scale}</div></div>
                  <div className="p-2 bg-[hsl(var(--muted))]/50 rounded"><div className="opacity-60">Shadow</div><div className="font-bold">×{generated.shadow_scale}</div></div>
                  <div className="p-2 bg-[hsl(var(--muted))]/50 rounded"><div className="opacity-60">Button</div><div className="font-bold">{generated.button_style}</div></div>
                </div>

                <div className="flex flex-col gap-2 pt-3 border-t border-white/10">
                  <Button onClick={applyTheme} className="w-full" data-testid="apply-theme-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" />Appliquer ce thème
                  </Button>
                  <Button onClick={downloadTheme} variant="outline" className="w-full">
                    <Copy className="w-4 h-4 mr-1.5" />Exporter .json
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Card className="p-4 bg-amber-500/10 border-amber-500/30">
        <div className="flex gap-3 items-start">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-xs space-y-1">
            <p><strong>Note éthique & légale :</strong> Le scraping d'URL extrait uniquement les directives visuelles publiques (couleurs, typographies, meta). Aucun texte, image ou contenu éditorial n'est copié. Utilisez cette fonction comme source d'<strong>inspiration</strong> — les thèmes générés sont des créations originales inspirées par l'ambiance du site source.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
